<script lang="ts" setup>
import { ref, computed, watchEffect } from 'vue';
import { getImageUrl } from '@/presentation/utils';

interface Props {
	src?: string;
	srcType?: 'public' | 'api' | 'fullPath';
	fallbackSrc?: string;
	fallbackSrcType?: 'public' | 'api' | 'fullPath';
	noPlaceHolder?: boolean;
	lightbox?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	srcType: 'api',
	fallbackSrcType: 'public',
	lightbox: false,
});

const defaultFallbackSrc = 'images/default-image.jpg';
const imageSrc = ref<string | undefined>(defaultFallbackSrc);
const isUsingFallback = ref(true);

const isExternalSource = (src: string) => /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(src) || /^(?:data|blob):/i.test(src);
const resolvePublicSource = (src: string) => {
	if (!src || isExternalSource(src)) return src;

	const baseUrl = import.meta.env.BASE_URL || '/';
	const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
	const normalizedSrc = src.replace(/^\/+/, '');

	return `${normalizedBase}${normalizedSrc}`;
};

const sourceResolvers: Record<string, (src: string) => string> = {
	public: resolvePublicSource,
	api: (src) => getImageUrl(src),
	fullPath: (src) => src,
};

const resolveImageSource = computed<string>({
	get() {
		const src = imageSrc.value || props.fallbackSrc || defaultFallbackSrc;
		const srcType = isUsingFallback.value ? props.fallbackSrcType : props.srcType;
		const resolver = sourceResolvers[srcType];
		return resolver ? resolver(src) : src;
	},
	set(value: string) {
		if (value?.trim()) {
			imageSrc.value = value;
			isUsingFallback.value = false;
			return;
		}

		imageSrc.value = props.fallbackSrc || defaultFallbackSrc;
		isUsingFallback.value = true;
	},
});

watchEffect(() => {
	resolveImageSource.value = props.src;
});

const handleImageError = () => {
	imageSrc.value = props.fallbackSrc || defaultFallbackSrc;
	isUsingFallback.value = true;
};

const isOpen = ref(false);
const openLightbox = () => {
	if (props.lightbox) isOpen.value = true;
};
const closeLightbox = () => {
	isOpen.value = false;
};
</script>

<template>
	<img
		v-bind="$attrs"
		:src="resolveImageSource"
		loading="lazy"
		@error="handleImageError"
		@click="openLightbox"
		:class="{ 'cursor-zoom-in': props.lightbox }"
		alt="Image"
	/>

	<teleport to="body">
		<div v-if="isOpen" class="fixed inset-0 w-full h-screen bg-black bg-opacity-80 flex items-center justify-center z-[10000]" @click.self="closeLightbox">
			<img :src="resolveImageSource" alt="Full Image" class="max-w-[90vw] max-h-[90vh] object-contain cursor-auto" @error="handleImageError" />
			<button class="fixed top-4 right-4 bg-transparent border-0 text-white text-[2rem] cursor-pointer" @click="closeLightbox">×</button>
		</div>
	</teleport>
</template>
